/**
 * Answering a challenge — `docs/identity-and-sign-in.md` §4.4, mockup board 4.
 *
 * Two parts, and the first one is the fix Aaron asked for:
 *
 *   1. **The switcher is always on screen** — one button per way in, above the
 *      panel, the picked one tinted. It used to be a text link called *Try
 *      another way* that **replaced** the panel with a list, so switching cost
 *      two clicks and a screen, and somebody whose phone was in the other room
 *      had no way of knowing an email code was even on offer until they went
 *      looking. A row of buttons says what the alternatives are without being
 *      asked, and switching is one click.
 *   2. **The panel** for whichever is picked: a button for a passkey, six boxes
 *      for the three that send digits, with Resend on a countdown.
 *
 * **The remembered method opens.** `localStorage` on this browser, falling back
 * to the strongest the person holds. It is a preference and never a fact the
 * server trusts — `preferredMethod` in `shared/src/mfa.ts` picks, and the
 * server accepts whatever comes back.
 *
 * It is a component rather than part of `sign-in.tsx` because §4.4 says the
 * same panels are what `useStepUp()` opens before a sensitive action and what
 * a switch between environments shows on arrival. One set of panels, three
 * places.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  MFA_KIND_INFO, byMfaOrder, preferredMethod, type MfaKind, type MfaMethod,
} from '@rowted/shared'
import { Button } from './primitives.js'
import { CodeInput } from './code-input.js'
import { IconNext, IconSecurity, IconSpinner } from '../frame/icons.js'
import { cn } from '../lib/cn.js'

/** Where the remembered pick lives. Per browser, never sent anywhere. */
const REMEMBERED = 'rowted.mfa.preferred'

const remember = (id: string): void => {
  try { window.localStorage.setItem(REMEMBERED, id) } catch { /* private window */ }
}
const remembered = (): string | null => {
  try { return window.localStorage.getItem(REMEMBERED) } catch { return null }
}

/**
 * What the person typed, as the code was issued.
 *
 * The hash on file is of the dashed form, so a code pasted without its dash
 * would be refused for no reason a person could see. Ten hex characters is the
 * whole alphabet, so putting the dash back is a formatting job, not a guess.
 */
export const formatRecovery = (typed: string): string => {
  const hex = typed.toLowerCase().replace(/[^0-9a-f]/g, '').slice(0, 10)
  return hex.length > 5 ? `${hex.slice(0, 5)}-${hex.slice(5)}` : hex
}

/** Recovery is offered here and is not a method anybody sets up. */
type Picked = { kind: MfaKind; method: MfaMethod } | { kind: 'recovery'; method: null }

export interface MfaAnswer {
  method: MfaKind | 'recovery'
  methodId?: string
  code?: string
  response?: unknown
}

export function MfaPanels({ methods, busy, onSend, onAnswer, onPasskey }: {
  methods: MfaMethod[]
  busy?: boolean
  /** Asks the server to send a fresh code. Returns how long until the next. */
  onSend: (kind: 'email' | 'sms') => Promise<number>
  onAnswer: (answer: MfaAnswer) => void
  /** Runs `navigator.credentials.get` and hands back the assertion. */
  onPasskey: () => Promise<unknown>
}) {
  const ordered = [...methods].sort(byMfaOrder)
  const [picked, setPicked] = useState<Picked | null>(() => {
    const first = preferredMethod(ordered, remembered())
    return first ? { kind: first.kind, method: first } : null
  })
  const [code, setCode] = useState('')

  /*
   * The Resend countdown. Started when a code goes out and ticked here rather
   * than by the server, because the server already refuses an early resend —
   * this only stops somebody pressing a button that is going to say no.
   */
  const [waitFor, setWaitFor] = useState(0)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    if (waitFor <= 0) return undefined
    timer.current = setInterval(() => setWaitFor((left) => Math.max(0, left - 1)), 1000)
    return () => { if (timer.current) clearInterval(timer.current) }
  }, [waitFor])

  const sendFor = useCallback(async (kind: 'email' | 'sms') => {
    const wait = await onSend(kind)
    setWaitFor(wait)
  }, [onSend])

  /* A code method opens by sending one — nobody wants to press Send first. */
  const sent = useRef<string | null>(null)
  useEffect(() => {
    if (!picked || picked.kind === 'recovery') return
    if (picked.kind !== 'email' && picked.kind !== 'sms') return
    if (sent.current === picked.method.id) return
    sent.current = picked.method.id
    void sendFor(picked.kind).catch(() => { /* the panel still takes a code */ })
  }, [picked, sendFor])

  const choose = (next: Picked) => {
    if (next.kind === picked?.kind) return
    setPicked(next)
    setCode('')
    if (next.method) remember(next.method.id)
  }

  /*
   * Every way in, in the order the setup dialog draws its cards, with Recovery
   * code last — it is the answer when all of the others have failed, and
   * somebody locked out of every one should not have to know it exists to find
   * it.
   */
  const ways: Picked[] = [
    ...ordered.map((method): Picked => ({ kind: method.kind, method })),
    { kind: 'recovery', method: null },
  ]

  if (!picked) {
    /* No method at all: a recovery code is the only thing left to offer, and
       the switcher would be a row of one. */
    return <RecoveryOnly code={code} onCode={setCode} busy={busy} onAnswer={onAnswer} />
  }

  return (
    <div className="flex flex-col gap-3">
      {ways.length > 1 && (
        <MethodSwitcher ways={ways} picked={picked} onPick={choose} />
      )}
      {picked.kind === 'passkey' && (
        <>
          <div className="flex items-center gap-2.5 rounded-popup border border-line
                          bg-surface-alt px-3.5 py-3">
            <IconSecurity size={16} />
            <span className="flex min-w-0 flex-col">
              <b className="text-card text-text">Use your passkey</b>
              <span className="truncate text-cell text-text-muted">{picked.method.hint}</span>
            </span>
          </div>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => {
              void onPasskey().then((response) => onAnswer({
                method: 'passkey', methodId: picked.method.id, response,
              }))
            }}
          >
            {busy ? <IconSpinner size={15} /> : <IconNext size={15} />}
            Continue with passkey
          </Button>
        </>
      )}

      {picked.kind !== 'passkey' && (
        <>
          <p className="m-0 text-cell leading-[1.45] text-text-muted">
            {promptFor(picked)}
          </p>
          {picked.kind === 'recovery' ? (
            /*
             * A text box, not the six boxes. A recovery code is
             * `generateRecoveryCodes`' five hex bytes as `a1b2c-3d4e5`, and
             * `CodeInput` strips everything but digits — every letter in it
             * would have vanished as it was typed.
             */
            <input
              value={code}
              onChange={(event) => setCode(formatRecovery(event.target.value))}
              aria-label="Recovery code"
              placeholder="a1b2c-3d4e5"
              spellCheck={false}
              autoComplete="off"
              autoFocus
              maxLength={11}
              className="h-control w-full rounded-field border border-line bg-field px-3
                         text-body tracking-[0.06em] text-text outline-none
                         focus:border-accent-line focus:shadow-ring"
            />
          ) : (
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={(full) => onAnswer({
                method: picked.kind,
                ...(picked.method ? { methodId: picked.method.id } : {}),
                code: full,
              })}
              /* Named for what it is asking for, because a screen reader
                 announcing "Code" over six boxes is not enough to go on. */
              label="Six-digit code"
              autoFocus
            />
          )}
          <Button
            variant="primary"
            /* Eleven, because `formatRecovery` has already put the dash back. */
            disabled={busy || code.length < (picked.kind === 'recovery' ? 11 : 6)}
            onClick={() => onAnswer({
              method: picked.kind,
              ...(picked.method ? { methodId: picked.method.id } : {}),
              code,
            })}
          >
            {busy ? <IconSpinner size={15} /> : <IconNext size={15} />}
            Continue
          </Button>
        </>
      )}

      {/* Resend only, now that the switcher is above rather than behind a
          link. Two ways to change method on one screen is one too many. */}
      {(picked.kind === 'email' || picked.kind === 'sms') && (
        <div className="flex items-center justify-center text-[11.5px]">
          <button
            type="button"
            disabled={waitFor > 0}
            onClick={() => void sendFor(picked.kind as 'email' | 'sms')}
            className={cn('font-semibold text-accent hover:text-accent-dark',
              'disabled:text-text-faint disabled:hover:text-text-faint')}
          >
            {waitFor > 0 ? `Resend in 0:${String(waitFor).padStart(2, '0')}` : 'Resend'}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * The row of ways in — board 4, rebuilt to Aaron's reference.
 *
 * Buttons rather than `Segmented`, because the track that control draws is
 * right for two or three short words of the *same* kind (Light · Dark · Auto)
 * and these are four unequal names, one of which is "Authenticator app". They
 * wrap, which a segmented track cannot; on a 380px sign-in card that is the
 * difference between four readable labels and four clipped ones.
 */
function MethodSwitcher({ ways, picked, onPick }: {
  ways: Picked[]
  picked: Picked
  onPick: (next: Picked) => void
}) {
  return (
    <div role="radiogroup" aria-label="How to verify" className="flex flex-wrap gap-1.5">
      {ways.map((way) => {
        const here = way.kind === picked.kind
        return (
          <button
            key={way.kind}
            type="button"
            role="radio"
            aria-checked={here}
            onClick={() => onPick(way)}
            className={cn('h-[30px] rounded-button border px-2.5 text-[12.5px]',
              'font-semibold transition-colors',
              here
                ? 'border-accent-line bg-accent-soft text-accent-dark'
                : 'border-line bg-surface text-text-muted hover:border-line-strong hover:text-text')}
          >
            {way.kind === 'recovery' ? 'Recovery code' : MFA_KIND_INFO[way.kind].name}
          </button>
        )
      })}
    </div>
  )
}

/**
 * An account with no method at all, answering with a recovery code.
 *
 * Rare and real: somebody whose only authenticator was reset still has the ten
 * codes they printed. The switcher would be a row of one, so it is left out
 * and the box is simply the box.
 */
function RecoveryOnly({ code, onCode, busy, onAnswer }: {
  code: string
  onCode: (next: string) => void
  busy?: boolean
  onAnswer: (answer: MfaAnswer) => void
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-cell leading-[1.45] text-text-muted">
        One of the ten codes you saved. Each works once.
      </p>
      <input
        value={code}
        onChange={(event) => onCode(formatRecovery(event.target.value))}
        aria-label="Recovery code"
        placeholder="a1b2c-3d4e5"
        spellCheck={false}
        autoComplete="off"
        autoFocus
        maxLength={11}
        className="h-control w-full rounded-field border border-line bg-field px-3
                   text-body tracking-[0.06em] text-text outline-none
                   focus:border-accent-line focus:shadow-ring"
      />
      <Button
        variant="primary"
        disabled={busy || code.length < 11}
        onClick={() => onAnswer({ method: 'recovery', code })}
      >
        {busy ? <IconSpinner size={15} /> : <IconNext size={15} />}
        Continue
      </Button>
    </div>
  )
}

function promptFor(picked: Picked): string {
  switch (picked.kind) {
    case 'totp': return 'From your authenticator app.'
    case 'email': return `Sent to ${picked.method.hint}. It works for 10 minutes.`
    case 'sms': return `Texted to ${picked.method.hint}. It works for 10 minutes.`
    default: return 'One of the ten codes you saved when you set two-step up.'
  }
}
